import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';
import { getErrorMessage } from '~shared/utils/error-handler';

import {
  V2_PAYMENT_BILLING_GUARD_PATH,
  V2_PAYMENT_CARDS_PATH,
  V2_PAYMENT_SETUP_CONFIG_PATH,
  v2PaymentCardDefaultPath,
  v2PaymentCardPath,
} from './v2-payment-card-paths';

export const SQUARE_SAVED_CARDS_QUERY_KEY = 'square-saved-cards';
export const SQUARE_SETUP_CONFIG_QUERY_KEY = 'square-setup-config';
export const PARENT_SQUARE_BILLING_GUARD_QUERY_KEY =
  'parent-square-billing-guard';

export interface ParentSquareBillingGuard {
  retainPaymentMethodWhileEnrolled: boolean;
  hasActiveEnrollments: boolean;
  savedMethodCount: number;
  canRemovePaymentMethod: boolean;
}

export interface SquareWebPaymentsConfig {
  provider: 'square';
  square: {
    applicationId: string;
    locationId: string;
    sandbox: boolean;
  };
}

export interface SquareSavedCard {
  id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  bankName?: string;
  isDefault?: boolean;
}

const PARENT_ID_QUERY = 'parentId';

const readPaymentErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const data = (error as { response?: { data?: { code?: unknown } } }).response
    ?.data;
  return typeof data?.code === 'string' ? data.code : undefined;
};

const rethrowPaymentRequestError = (error: unknown, fallback: string): never => {
  const wrapped = new Error(getErrorMessage(error, fallback)) as Error & {
    code?: string;
  };
  wrapped.code = readPaymentErrorCode(error);
  throw wrapped;
};

export const getSquareSetupConfig =
  async (): Promise<SquareWebPaymentsConfig> => {
    try {
      const response = await baseAPI.get<SquareWebPaymentsConfig>(
        V2_PAYMENT_SETUP_CONFIG_PATH,
      );
      return response.data;
    } catch (error) {
      return rethrowPaymentRequestError(
        error,
        'Could not load Square payment setup',
      );
    }
  };

export const listParentSquareSavedCards = async (
  parentId: string,
): Promise<SquareSavedCard[]> => {
  try {
    const response = await baseAPI.get<SquareSavedCard[]>(
      V2_PAYMENT_CARDS_PATH,
      { params: { [PARENT_ID_QUERY]: parentId } },
    );
    return response.data ?? [];
  } catch (error) {
    return rethrowPaymentRequestError(
      error,
      'Could not load saved cards for this parent',
    );
  }
};

export const createSquareSavedCard = async (body: {
  parentId: string;
  sourceId: string;
  verificationToken?: string;
}): Promise<SquareSavedCard> => {
  try {
    const response = await baseAPI.post<SquareSavedCard>(
      V2_PAYMENT_CARDS_PATH,
      body,
    );
    return response.data;
  } catch (error) {
    return rethrowPaymentRequestError(error, 'Failed to save payment method');
  }
};

export const getParentSquareBillingGuard = async (
  parentId: string,
): Promise<ParentSquareBillingGuard> => {
  try {
    const response = await baseAPI.get<ParentSquareBillingGuard>(
      V2_PAYMENT_BILLING_GUARD_PATH,
      { params: { [PARENT_ID_QUERY]: parentId } },
    );
    return response.data;
  } catch (error) {
    return rethrowPaymentRequestError(
      error,
      'Could not load payment method rules for this parent',
    );
  }
};

export const disableParentSquareSavedCard = async (input: {
  parentId: string;
  cardId: string;
}): Promise<void> => {
  try {
    await baseAPI.delete(v2PaymentCardPath(input.cardId), {
      params: { [PARENT_ID_QUERY]: input.parentId },
    });
  } catch (error) {
    return rethrowPaymentRequestError(
      error,
      'Failed to remove this payment method',
    );
  }
};

export const setParentSquareDefaultCard = async (input: {
  parentId: string;
  cardId: string;
}): Promise<SquareSavedCard> => {
  try {
    const response = await baseAPI.patch<SquareSavedCard>(
      v2PaymentCardDefaultPath(input.cardId),
      undefined,
      { params: { [PARENT_ID_QUERY]: input.parentId } },
    );
    return response.data;
  } catch (error) {
    return rethrowPaymentRequestError(
      error,
      'Failed to set the default card for this parent',
    );
  }
};

/** Invalidate list/guard after add/remove/default for Square or Stripe (V2 cards API). */
export const invalidateParentSquareCardQueries = (parentId: string) => {
  void queryClient.invalidateQueries({ queryKey: [KEYS.CARD] });
  void queryClient.invalidateQueries({ queryKey: [KEYS.PAYMENT_METHOD] });
  void queryClient.invalidateQueries({
    queryKey: [SQUARE_SAVED_CARDS_QUERY_KEY, parentId],
  });
  void queryClient.invalidateQueries({
    queryKey: [PARENT_SQUARE_BILLING_GUARD_QUERY_KEY, parentId],
  });
};

export const useGetSquareSetupConfig = (enabled: boolean) => {
  return useQuery({
    queryKey: [SQUARE_SETUP_CONFIG_QUERY_KEY],
    queryFn: getSquareSetupConfig,
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useListParentSquareSavedCards = (
  parentId: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [SQUARE_SAVED_CARDS_QUERY_KEY, parentId],
    queryFn: () => listParentSquareSavedCards(parentId),
    enabled: enabled && !!parentId,
  });
};

export const useCreateSquareSavedCard = () => {
  return useMutation({
    mutationFn: createSquareSavedCard,
    onSuccess: (_data, variables) => {
      invalidateParentSquareCardQueries(variables.parentId);
    },
  });
};

export const useSetParentSquareDefaultCard = () => {
  return useMutation({
    mutationFn: setParentSquareDefaultCard,
    onSuccess: (_data, variables) => {
      invalidateParentSquareCardQueries(variables.parentId);
    },
  });
};

export const useGetParentSquareBillingGuard = (
  parentId: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [PARENT_SQUARE_BILLING_GUARD_QUERY_KEY, parentId],
    queryFn: () => getParentSquareBillingGuard(parentId),
    enabled: enabled && !!parentId,
  });
};

export const useDisableParentSquareSavedCard = () => {
  return useMutation({
    mutationFn: disableParentSquareSavedCard,
    onSuccess: (_data, variables) => {
      invalidateParentSquareCardQueries(variables.parentId);
    },
  });
};

